import Animated, { FadeIn, FadeOut, SlideInDown, SlideOutDown } from 'react-native-reanimated';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Avatar } from '@/components/avatar';
import { bioFor } from '@/game/bios';
import type { Cohort, Person } from '@/game/types';
import { colors, radius, type } from '@/theme';

/** Ceiling for the sheet, so a long bio never swallows the whole screen. */
const SHEET_MAX_PCT = 86;

type Props = {
  person: Person;
  /** The cohort they were drawn from — the derived fallback leans on it. */
  cohort: Cohort;
  onClose: () => void;
};

/**
 * "Who is this?" — held open by a long press on the draft board, for the player
 * who doesn't recognise a name. Deliberately a biography and not a stat sheet:
 * the game keeps its numbers hidden, and this answers the human question
 * instead.
 *
 * The identity block sits outside the scroller so the avatar can hang off the
 * cover band without being clipped by it.
 */
export function PersonCard({ person, cohort, onClose }: Props) {
  const insets = useSafeAreaInsets();
  const bio = bioFor(person, cohort);

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <Animated.View entering={FadeIn.duration(160)} exiting={FadeOut.duration(140)} style={styles.scrim}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
      </Animated.View>

      <Animated.View
        entering={SlideInDown.duration(260)}
        exiting={SlideOutDown.duration(180)}
        style={[styles.sheet, { paddingBottom: insets.bottom + 8, maxHeight: `${SHEET_MAX_PCT}%` }]}>
        <View style={[styles.cover, { backgroundColor: cohort.accent }]} />

        <Pressable style={styles.close} onPress={onClose} hitSlop={12}>
          <Text style={styles.closeText}>✕</Text>
        </Pressable>

        <View style={styles.identity}>
          <View style={styles.avatarRing}>
            <Avatar initials={person.initials} size={78} />
          </View>
          <Text style={styles.name}>{person.name}</Text>
          <Text style={styles.headline}>{bio.headline}</Text>
          {bio.location && <Text style={styles.location}>{bio.location}</Text>}
        </View>

        <ScrollView
          style={styles.scroller}
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}>
          <Text style={[styles.sectionTitle, styles.firstSection]}>About</Text>
          <Text style={styles.about}>{bio.about}</Text>

          {bio.experience.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Experience</Text>
              <View style={styles.expList}>
                {bio.experience.map((item) => (
                  <View key={`${item.org}-${item.role}`} style={styles.expRow}>
                    <View style={styles.expLogo}>
                      <Text style={styles.expLogoText}>{item.org.slice(0, 1)}</Text>
                    </View>
                    <View style={styles.expCopy}>
                      <Text style={styles.expRole}>{item.role}</Text>
                      <Text style={styles.expOrg}>{item.org}</Text>
                      {item.years && <Text style={styles.expYears}>{item.years}</Text>}
                      {item.note && <Text style={styles.expNote}>{item.note}</Text>}
                    </View>
                  </View>
                ))}
              </View>
            </>
          )}

          <Text style={styles.sectionTitle}>Skills</Text>
          <View style={styles.skills}>
            {person.tags.map((tag) => (
              <Text key={tag} style={styles.skill}>
                {tag}
              </Text>
            ))}
          </View>
        </ScrollView>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(6, 6, 7, 0.86)',
  },
  sheet: {
    // Hugs its content and stops at `SHEET_MAX_PCT`. A fixed-height sheet looks
    // broken around a short derived bio and cramped around a long written one.
    marginTop: 'auto',
    backgroundColor: colors.card,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  cover: {
    height: 84,
    opacity: 0.85,
  },
  close: {
    position: 'absolute',
    top: 14,
    right: 16,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(10,10,11,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  closeText: {
    color: colors.text,
    fontSize: 14,
  },
  identity: {
    paddingHorizontal: 20,
    marginTop: -42,
  },
  avatarRing: {
    alignSelf: 'flex-start',
    padding: 4,
    borderRadius: 32,
    backgroundColor: colors.card,
    marginBottom: 12,
  },
  name: {
    ...type.title,
    fontSize: 26,
    color: colors.text,
  },
  headline: {
    ...type.body,
    fontSize: 15,
    color: colors.text,
    marginTop: 4,
    lineHeight: 21,
  },
  location: {
    ...type.body,
    fontSize: 13,
    color: colors.dim,
    marginTop: 6,
  },
  scroller: {
    flexShrink: 1,
  },
  scroll: {
    paddingHorizontal: 20,
    paddingBottom: 28,
  },
  sectionTitle: {
    ...type.label,
    fontSize: 11,
    color: colors.dim,
    marginTop: 26,
    marginBottom: 10,
  },
  firstSection: {
    marginTop: 20,
  },
  about: {
    ...type.body,
    fontSize: 15,
    lineHeight: 23,
    color: colors.text,
  },
  expList: {
    gap: 18,
  },
  expRow: {
    flexDirection: 'row',
    gap: 14,
  },
  expLogo: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: colors.cardHi,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expLogoText: {
    ...type.title,
    fontSize: 19,
    color: colors.dim,
  },
  expCopy: {
    flex: 1,
    gap: 1,
  },
  expRole: {
    ...type.body,
    fontWeight: '700',
    fontSize: 15,
    color: colors.text,
  },
  expOrg: {
    ...type.body,
    fontSize: 14,
    color: colors.dim,
  },
  expYears: {
    ...type.body,
    fontSize: 12,
    color: colors.faint,
  },
  expNote: {
    ...type.body,
    fontSize: 13,
    color: colors.dim,
    marginTop: 5,
    lineHeight: 19,
  },
  skills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  skill: {
    ...type.label,
    fontSize: 10,
    color: colors.dim,
    backgroundColor: colors.cardHi,
    borderRadius: radius.pill,
    paddingHorizontal: 11,
    paddingVertical: 6,
    overflow: 'hidden',
  },
});
